import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import {
  EmptyState,
  ErrorState,
  KeyValues,
  LoadingRows,
  Mono,
  PageHeader,
  StatusBadge,
} from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TaskActions } from "./task-actions";
import { dateTime, money, shortId, type Money } from "@/lib/format";
import { label } from "@/lib/labels";

interface TaskRow {
  id: string;
  status: string;
  service: string;
  customerId: string;
  provider: string | null;
  scheduledStart: string | null;
  createdAt: string;
}

const STATUSES = [
  "requested",
  "accepted",
  "in_progress",
  "invoiced",
  "completed",
  "disputed",
  "cancelled",
  "expired",
  "draft",
];

const columns: ColumnDef<TaskRow, unknown>[] = [
  { accessorKey: "id", header: "İş", cell: ({ row }) => <Mono>{shortId(row.original.id)}</Mono> },
  { accessorKey: "service", header: "Hizmet" },
  {
    accessorKey: "status",
    header: "Durum",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "provider",
    header: "Hizmet veren",
    cell: ({ row }) => row.original.provider ?? "—",
  },
  {
    accessorKey: "scheduledStart",
    header: "Zaman",
    cell: ({ row }) => dateTime(row.original.scheduledStart),
  },
  {
    accessorKey: "createdAt",
    header: "Oluşturuldu",
    cell: ({ row }) => dateTime(row.original.createdAt),
  },
];

export function TasksPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const customer = params.get("customer") ?? "";
  const navigate = useNavigate();
  const qs = new URLSearchParams({ limit: "100" });
  if (status) qs.set("status", status);
  if (customer) qs.set("customer", customer);
  const q = useQuery({
    queryKey: ["admin-tasks", status, customer],
    queryFn: () => api<{ tasks: TaskRow[] }>(`/v1/admin/tasks?${qs}`),
  });
  return (
    <>
      <PageHeader
        title="İşler"
        description={customer ? `Müşteri ${shortId(customer)} işleri` : "En yeni 100 iş"}
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("status", e.target.value);
              else next.delete("status");
              setParams(next);
            }}
          >
            <option value="">Tüm durumlar</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {label(s) !== s ? label(s) : s}
              </option>
            ))}
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.tasks}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        onRowClick={(t) => navigate(`/tasks/${t.id}`)}
        empty="Bu filtrede iş yok."
      />
    </>
  );
}

interface TaskDetail {
  id: string;
  status: string;
  service: string;
  customerId: string;
  providerId: string | null;
  provider: string | null;
  assignedMemberId: string | null;
  scheduledStart: string | null;
  scheduledMinutes: number | null;
  createdAt: string;
  history: {
    from_status: string | null;
    to_status: string;
    actor_user_id: string | null;
    note: string | null;
    created_at: string;
  }[];
  invoices: {
    id: string;
    status: string;
    receiptNumber: string | null;
    total: Money | null;
    customerFees: Money | null;
    providerPayout: Money | null;
    confirmationType: string | null;
    providerReceipt: { path: string; number: string | null; uploadedAt: string } | null;
    createdAt: string;
  }[];
  payments: {
    id: string;
    type: string;
    status: string;
    amount: Money | null;
    failureReason: string | null;
    processorId: string | null;
    createdAt: string;
  }[];
  payouts: {
    id: string;
    status: string;
    amount: Money | null;
    scheduledFor: string | null;
    paidAt: string | null;
    transferId: string | null;
  }[];
  disputes: {
    id: string;
    stage: string;
    reason: string;
    status: string;
    resolution: string | null;
    refund_minor: string | null;
    opened_at: string;
  }[];
  cancellation: { by: string; reason: string | null; fee: Money | null; createdAt: string } | null;
  mediaCount: number;
  members: { id: string; role: string; name: string }[];
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Rows<T>({
  rows,
  render,
  empty,
}: {
  rows: T[];
  render: (r: T) => React.ReactNode;
  empty: string;
}) {
  if (!rows.length) return <EmptyState>{empty}</EmptyState>;
  return (
    <ul className="divide-y">
      {rows.map((r, i) => (
        <li key={i} className="py-2.5 text-sm">
          {render(r)}
        </li>
      ))}
    </ul>
  );
}

export function TaskDetailPage() {
  const { id = "" } = useParams();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ["admin-task", id],
    queryFn: () => api<TaskDetail>(`/v1/admin/tasks/${id}`),
  });
  const back = <BackLink to="/tasks">İşler</BackLink>;
  if (q.isPending)
    return (
      <>
        {back}
        <LoadingRows rows={8} />
      </>
    );
  if (q.error)
    return (
      <>
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  const t = q.data;
  return (
    <>
      <PageHeader
        back={back}
        title={
          <span className="flex items-center gap-3">
            {t.service} <StatusBadge status={t.status} />
          </span>
        }
        description={<Mono>{t.id}</Mono>}
        actions={can("tasks.manage") ? <TaskActions task={t} /> : null}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <Section title="Özet">
            <KeyValues
              items={[
                [
                  "Müşteri",
                  <Link
                    key="c"
                    className="text-primary hover:underline"
                    to={`/users/${t.customerId}`}
                  >
                    {shortId(t.customerId)}
                  </Link>,
                ],
                ["Hizmet veren", t.provider ?? "—"],
                ["Zaman", dateTime(t.scheduledStart)],
                ["Süre (tahmini)", t.scheduledMinutes ? `${t.scheduledMinutes} dk` : "—"],
                ["Fotoğraf", t.mediaCount],
                ["Oluşturuldu", dateTime(t.createdAt)],
                ...(t.cancellation
                  ? ([
                      ["İptal eden", label(t.cancellation.by)],
                      ["İptal ücreti", money(t.cancellation.fee)],
                    ] as [React.ReactNode, React.ReactNode][])
                  : []),
              ]}
            />
          </Section>
        </div>
        <div className="lg:col-span-2">
          <Tabs defaultValue="history" className="grid gap-4">
            <TabsList className="w-fit flex-wrap">
              <TabsTrigger value="history">Geçmiş</TabsTrigger>
              <TabsTrigger value="invoices">Faturalar ({t.invoices.length})</TabsTrigger>
              <TabsTrigger value="payments">Ödemeler ({t.payments.length})</TabsTrigger>
              <TabsTrigger value="payouts">Aktarımlar ({t.payouts.length})</TabsTrigger>
              <TabsTrigger value="disputes">İtiraz ({t.disputes.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="history">
              <Section title="Durum geçmişi">
                <Rows
                  rows={t.history}
                  empty="Kayıt yok."
                  render={(h) => (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <StatusBadge status={h.from_status} /> →{" "}
                        <StatusBadge status={h.to_status} />
                        {h.note && <span className="text-muted-foreground">{h.note}</span>}
                      </span>
                      <span className="text-muted-foreground">{dateTime(h.created_at)}</span>
                    </div>
                  )}
                />
              </Section>
            </TabsContent>
            <TabsContent value="invoices">
              <Section title="Faturalar">
                <Rows
                  rows={t.invoices}
                  empty="Fatura yok."
                  render={(i) => (
                    <div className="grid gap-1 sm:grid-cols-4">
                      <span className="flex items-center gap-2">
                        <StatusBadge status={i.status} />{" "}
                        <Mono>{i.receiptNumber ?? shortId(i.id)}</Mono>
                      </span>
                      <span>
                        Toplam <b className="tabular">{money(i.total)}</b>
                      </span>
                      <span className="text-muted-foreground">
                        Poji ücreti {money(i.customerFees)}
                      </span>
                      <span className="text-muted-foreground">
                        Hizmet verene {money(i.providerPayout)}
                      </span>
                      {i.confirmationType !== "cancellation" &&
                        ["approved", "charged"].includes(i.status) && (
                          <span className="text-xs sm:col-span-4">
                            {i.providerReceipt ? (
                              <span className="text-muted-foreground">
                                Hizmet verenin fişi yüklendi
                                {i.providerReceipt.number &&
                                  ` (${i.providerReceipt.number})`} ·{" "}
                                {dateTime(i.providerReceipt.uploadedAt)}
                              </span>
                            ) : (
                              <Badge variant="warning">Hizmet verenin fişi eksik</Badge>
                            )}
                          </span>
                        )}
                    </div>
                  )}
                />
              </Section>
            </TabsContent>
            <TabsContent value="payments">
              <Section title="Ödemeler (Stripe)">
                <Rows
                  rows={t.payments}
                  empty="Ödeme kaydı yok."
                  render={(p) => (
                    <div className="grid gap-1 sm:grid-cols-4">
                      <span>{label(p.type)}</span>
                      <span>
                        <StatusBadge status={p.status} />
                      </span>
                      <span className="tabular">{money(p.amount)}</span>
                      <span className="text-muted-foreground">
                        {p.failureReason ?? dateTime(p.createdAt)}
                      </span>
                    </div>
                  )}
                />
              </Section>
            </TabsContent>
            <TabsContent value="payouts">
              <Section title="Hizmet veren aktarımları">
                <Rows
                  rows={t.payouts}
                  empty="Aktarım yok."
                  render={(p) => (
                    <div className="grid gap-1 sm:grid-cols-4">
                      <StatusBadge status={p.status} />
                      <span className="tabular">{money(p.amount)}</span>
                      <span className="text-muted-foreground">
                        Plan: {dateTime(p.scheduledFor)}
                      </span>
                      <span className="text-muted-foreground">
                        {p.paidAt ? `Ödendi: ${dateTime(p.paidAt)}` : ""}
                      </span>
                    </div>
                  )}
                />
              </Section>
            </TabsContent>
            <TabsContent value="disputes">
              <Section title="İtirazlar">
                <Rows
                  rows={t.disputes}
                  empty="İtiraz yok."
                  render={(d) => (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <StatusBadge status={d.status} /> {label(d.reason)}
                      </span>
                      <Link className="text-primary hover:underline" to={`/disputes/${d.id}`}>
                        Aç
                      </Link>
                    </div>
                  )}
                />
              </Section>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
